import { useCallback, useEffect, useRef, useState } from "react";
import { io, type Socket } from "socket.io-client";
import type { BjBonusStatus, BjChatMessage, PkAction, PkGameUpdate, PkSession } from "@ziolo/shared";

const EMPTY_TABLE: PkGameUpdate = {
  phase: "waiting",
  pot: 0,
  currentBet: 0,
  communityCards: [],
  players: [],
  waitingList: [],
  currentUserId: null,
  dealerSeatIndex: null,
  seatedCount: 0,
  turnEndsAt: null,
  handEndsAt: null,
  toCall: 0,
  minRaiseTo: 0,
  legalActions: [],
  winners: [],
};

export function usePokerSocket(enabled: boolean) {
  const socketRef = useRef<Socket | null>(null);
  const [table, setTable] = useState<PkGameUpdate>(EMPTY_TABLE);
  const [session, setSession] = useState<PkSession | null>(null);
  const [chat, setChat] = useState<BjChatMessage[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletEpoch, setWalletEpoch] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const socket = io(`${window.location.origin}/poker`, {
      path: "/socket.io/",
      withCredentials: true,
      transports: ["polling", "websocket"],
      forceNew: true,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      setError(null);
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", (err: Error) => {
      setConnected(false);
      setError(
        err.message === "UNAUTHORIZED"
          ? "Sesja wygasła. Zaloguj się ponownie."
          : "Nie udało się połączyć ze stołem. Odśwież stronę.",
      );
    });
    socket.on("game_update", (update: PkGameUpdate) => setTable(update));
    socket.on("session", (next: PkSession) => setSession(next));
    socket.on("chat:init", (payload: { messages: BjChatMessage[] }) => {
      setChat(payload.messages);
    });
    socket.on("chat:new", (message: BjChatMessage) => {
      setChat((prev) => [...prev, message].slice(-150));
    });
    socket.on("daily:status", (status: BjBonusStatus) => {
      setSession((prev) => ({ seated: prev?.seated ?? false, ...status }));
    });
    socket.on("daily:claimed", (result: { ok: boolean; chips?: number; nextAvailableAt?: number | null }) => {
      if (result.ok) {
        setWalletEpoch((n) => n + 1);
        setSession((prev) => ({
          seated: prev?.seated ?? false,
          canClaim: false,
          nextAvailableAt: result.nextAvailableAt ?? Date.now() + 2 * 60 * 60 * 1000,
          amount: 2137,
          chips: result.chips ?? prev?.chips ?? 0,
          keys: prev?.keys ?? 0,
          equippedSkin: prev?.equippedSkin ?? "classic",
        }));
      }
    });
    socket.on("notice", (payload: { text: string }) => {
      setNotice(payload.text);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [enabled]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  const emit = useCallback((event: string, payload?: unknown) => {
    const socket = socketRef.current;
    if (!socket) return;
    if (payload === undefined) socket.emit(event);
    else socket.emit(event, payload);
  }, []);

  return {
    table,
    session,
    chat,
    notice,
    connected,
    error,
    walletEpoch,
    join: (seatIndex?: number) => emit("join_game", seatIndex),
    leave: () => emit("leave_game"),
    act: (action: PkAction, amount?: number) =>
      emit("player_action", amount === undefined ? { action } : { action, amount }),
    sendChat: (text: string) => emit("chat:send", { text, cid: crypto.randomUUID() }),
    claimBonus: () => emit("daily:claim"),
  };
}
